import {
  type InstalledWidget,
  type Widget,
  type WidgetSetting,
  type WidgetSizeDefinition,
} from "@repo/api";
import { Button, ListView, Text } from "@repo/skapa";
import { T } from "@repo/translation";
import {
  ButtonsContainer,
  ElementsContainer,
  SectionContainer,
} from "@repo/ui";
import { useState } from "react";

import { isInstalledWidget } from "../../widgets/widgetSettings/widgetsSettings";
import { ClearAllSettingsButton } from "./ClearAllSettingsButton";
import { SettingsControl } from "./SettingsControl";
import { getVisibleSettings } from "./utils";

export const WidgetSettings = ({
  widget,
  onUpdateWidget,
  onUpdateWidgetSize,
}: {
  widget?: Widget | InstalledWidget;
  onUpdateWidget: (w: InstalledWidget) => Promise<void>;
  onUpdateWidgetSize: (
    widgetId: number,
    size: WidgetSizeDefinition
  ) => Promise<void>;
}) => {
  const isInstalled = isInstalledWidget(widget);

  const defaultSize = isInstalled
    ? widget.sizeDefinition.find(
        (size) =>
          size.col === widget.widgetSettings.appSize?.col &&
          size.row === widget.widgetSettings.appSize?.row
      )
    : widget?.sizeDefinition[0];
  const defaultSetting = isInstalled ? widget.widgetSettings.appSetting : {};

  const [selectedSize, setSelectedSize] = useState<
    WidgetSizeDefinition | undefined
  >(defaultSize);

  const [settings, setSettings] = useState<WidgetSetting>(defaultSetting ?? {});

  if (!widget) {
    return (
      <SectionContainer>
        <T translationKey="label.no_widget_selected" />
      </SectionContainer>
    );
  }

  return (
    <SectionContainer>
      <SectionContainer>
        <Text textStyle="Heading.S">
          <T translationKey="label.widget_size" />
        </Text>
        <Text>
          <T translationKey="prompt.widget_size_desc" />
        </Text>
        <ButtonsContainer justifyContent="start">
          {widget.sizeDefinition.map((size) => (
            <div key={size.size}>
              <Button
                text={size.size}
                size="small"
                type={
                  selectedSize?.size === size.size ? "primary" : "secondary"
                }
                onClick={() => {
                  setSelectedSize(size);
                  if (isInstalled) {
                    onUpdateWidgetSize(widget.workspacePreferenceId, size);
                  }
                }}
              />
            </div>
          ))}
          <ClearAllSettingsButton
            isInstalled={isInstalled}
            widget={widget as InstalledWidget}
            setSettings={setSettings}
            onUpdateWidget={onUpdateWidget}
          />
        </ButtonsContainer>
        {isInstalled && (widget.settingsDefinition?.length ?? 0) > 0 && (
          <ElementsContainer>
            <Text textStyle="Heading.S">
              <T translationKey="label.optional_filters" />
            </Text>
            <ListView id="widget-settings">
              {getVisibleSettings(
                widget.settingsDefinition,
                widget.widgetSettings.filters
              ).map((settingDefinition) => {
                return (
                  <SettingsControl
                    key={settingDefinition.id}
                    settingDefinition={settingDefinition}
                    setSetting={(newSettings) => {
                      setSettings(newSettings);

                      if (isInstalled) {
                        onUpdateWidget({
                          ...widget,
                          widgetSettings: {
                            ...widget.widgetSettings,
                            appSetting: {
                              ...newSettings,
                            },
                          },
                        });
                      }
                    }}
                    setting={settings}
                  />
                );
              })}
            </ListView>
          </ElementsContainer>
        )}
      </SectionContainer>
    </SectionContainer>
  );
};
